import React, { useState, useEffect, useCallback } from "react";
import axios from "../../../../../../../api/axios";
import { DataGrid } from "@mui/x-data-grid";
import { Snackbar, Alert } from "@mui/material";
import ConfirmDeleteModal from "../../../../../../elements/modal/modal";
import ApiLoader from "../../../../../../elements/isloading";

const Commission = () => {
  const business_entity_id = sessionStorage.getItem("configBizEntityId");
  const [mappings, setMappings] = useState([]);
  const [users, setUsers] = useState([]);
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [formData, setFormData] = useState({
    user_id: "",
    item_id: "",
    commission_charge: "",
  });

  const fetchMappings = async () => {
    setApiLoading(true);
    try {
      const response = await axios.get(
        `retail/commission/${business_entity_id}`,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        },
      );
      setMappings(response.data || []);
    } catch (error) {
      console.error("Error fetching mappings:", error);
    }
    setApiLoading(false);
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(
        `/businessEntities/getAllBusinessEntityUsers/${business_entity_id}`,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        },
      );
      setUsers(response.data?.allUsers || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [business_entity_id]);

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(
        `retail/products/${business_entity_id}`,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        },
      );
      setItems(response.data || []);
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  }, [business_entity_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    setApiLoading(true);

    try {
      const payload = {
        ...formData,
        business_entity_id: business_entity_id,
      };

      if (editMode) {
        await axios.put(
          `retail/commission-mapping/${selectedMapping.id}`,
          payload,
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: false,
          },
        );
      } else {
        await axios.post("retail/commission-mapping", payload, {
          headers: { "Content-Type": "application/json" },
          withCredentials: false,
        });
      }

      fetchMappings();
      resetForm();
      setShowModal(false);
      setSnackbar({
        open: true,
        message: editMode
          ? "Commission mapping updated successfully!"
          : "Commission mapping added successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving mapping:", error);
      let errorMessage = "An error occurred while saving the mapping";

      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
    setApiLoading(false);
  };

  const handleEdit = (mapping) => {
    setFormData({
      user_id: mapping.user_id,
      item_id: mapping.item_id,
      commission_charge: mapping.commission_charge,
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    setApiLoading(true);
    try {
      await axios.delete(`retail/commission-mapping/${id}`, {
        headers: { "Content-Type": "application/json" },
        withCredentials: false,
      });
      setMappings(mappings.filter((mapping) => mapping.id !== id));
    } catch (error) {
      console.error("Error deleting mapping:", error);
    }
    setApiLoading(false);
  };

  const resetForm = () => {
    setFormData({
      user_id: "",
      item_id: "",
      commission_charge: "",
    });
    setSelectedMapping(null);
    setEditMode(false);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchMappings();
    fetchUsers();
    fetchItems();
  }, [fetchUsers, fetchItems]);

  const columns = [
    {
      field: "serial",
      headerName: "S.No",
      width: 70,
      renderCell: (params) => {
        return params.api.getRowIndexRelativeToVisibleRows(params.row.id) + 1;
      },
    },
    {
      field: "user_name",
      headerName: "User",
      width: 150,
      renderCell: (params) => {
        const user = users.find((u) => u.userid === params.row.user_id);
        return user?.name || user?.email || "Unknown";
      },
    },
    {
      field: "item_name",
      headerName: "Item",
      width: 150,
      renderCell: (params) => {
        const item = items.find((i) => i.id === params.row.item_id);
        return item?.name || "Unknown";
      },
    },
    {
      field: "commission_charge",
      headerName: "Commission Charge",
      width: 150,
      renderCell: (params) =>
        `${params.row.commission_charge}%`,
    },
    {
      field: "action",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => {
        return (
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-primary d-flex align-items-center"
              onClick={() => handleEdit(params.row)}
              style={{ fontSize: "0.875rem", padding: "0.25rem 0.75rem" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="me-1"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              Edit
            </button>
            <button
              className="btn btn-sm btn-danger d-flex align-items-center"
              onClick={() => setSelectedMapping(params.row)}
              style={{ fontSize: "0.875rem", padding: "0.25rem 0.75rem" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="me-1"
              >
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              Delete
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex-grow-1">
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="h4 mb-0">Commission Mappings</h2>
          <button
            className="btn btn-success"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            Add New Mapping
          </button>
        </div>

        <div className="card shadow-sm">
          <div className="card-body">
            <div style={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={mappings}
                columns={columns}
                getRowId={(row) => row.id}
                pageSize={10}
                rowsPerPageOptions={[10]}
                disableSelectionOnClick
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <>
          {/* Backdrop */}
          <div className="modal fade show"></div>

          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content shadow-lg border-0">
                {/* Header */}
                <div className="modal-header px-4 py-3">
                  <h5 className="modal-title fw-bold">
                    {editMode
                      ? "Edit Commission Mapping"
                      : "Add New Commission Mapping"}
                  </h5>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                    style={{
                      width: "32px",
                      height: "32px",
                      fontSize: "16px",
                      opacity: "0.75",
                      transition: "opacity 0.2s",
                    }}
                  />
                </div>
                {/* Body */}
                <div className="modal-body px-4 py-3">
                  <div className="row g-3">
                    {/* User */}
                    <div className="col-md-6">
                      <label htmlFor="user_id" className="form-label">
                        User
                      </label>
                      <select
                        className="form-select"
                        id="user_id"
                        name="user_id"
                        value={formData.user_id}
                        onChange={handleInputChange}
                        required
                        disabled={editMode}
                      >
                        <option value="">Select User</option>
                        {users.map((user) => (
                          <option key={user.userid} value={user.userid}>
                            {user.name || user.email}
                          </option>
                        ))}
                      </select>
                      {editMode && (
                        <small className="text-muted">
                          User cannot be changed in edit mode
                        </small>
                      )}
                    </div>

                    {/* Item */}
                    <div className="col-md-6">
                      <label htmlFor="item_id" className="form-label">
                        Item
                      </label>
                      <select
                        className="form-select"
                        id="item_id"
                        name="item_id"
                        value={formData.item_id}
                        onChange={handleInputChange}
                        required
                        disabled={editMode}
                      >
                        <option value="">Select Item</option>
                        {items.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name} - ₹
                            {parseFloat(item.price || 0).toFixed(2)}
                          </option>
                        ))}
                      </select>
                      {editMode && (
                        <small className="text-muted">
                          Item cannot be changed in edit mode
                        </small>
                      )}
                    </div>

                    {/* Commission */}
                    <div className="col-md-12">
                      <label htmlFor="commission_charge" className="form-label">
                        Commission Charge In %
                      </label>

                      <div className="input-group">
                        <input
                          type="text"
                          step="0.01"
                          min="0"
                          className="form-control"
                          id="commission_charge"
                          name="commission_charge"
                          value={formData.commission_charge}
                          onChange={handleInputChange}
                          placeholder="0.00"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="modal-footer px-4 py-3">
                  <button
                    type="button"
                    className="btn btn-light border"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={() => handleSubmit()}
                    className="btn btn-primary"
                  >
                    {editMode ? "Update" : "Save"} Mapping
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {selectedMapping && !showModal && (
        <ConfirmDeleteModal
          setOpenModal={setSelectedMapping}
          message={`Are you sure you want to delete this commission mapping?`}
          description="You won't be able to revert this!"
          onDelete={() => handleDelete(selectedMapping.id)}
        />
      )}

      {apiLoading && <ApiLoader />}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Commission;
